import React, { useEffect, useState } from 'react';
import axios from 'axios';

const OrderEdit = () => {
  const [orders, setOrders] = useState([]); // List of all orders
  const [selectedOrderId, setSelectedOrderId] = useState(''); // Selected order ID
  const [formData, setFormData] = useState(null); // Order details for editing
  const [products, setProducts] = useState([]); // List of all products
  const [teamMembers, setTeamMembers] = useState([]); // List of all team members
  const [loading, setLoading] = useState(false);

  // Fetch all orders for the dropdown
  useEffect(() => {
    const fetchOrders = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const response = await axios.get('https://astrovidhya.in/api/orders', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setOrders(response.data);
      } catch (err) {
        console.error('Error fetching orders:', err.response?.data || err.message);
        alert('Failed to load orders.');
      }
    };
    fetchOrders();
  }, []);

  // Fetch products and team members for dropdowns
  useEffect(() => {
    const fetchMetadata = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const [productsResponse, teamMembersResponse] = await Promise.all([
          axios.get('https://astrovidhya.in/api/products', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get('https://astrovidhya.in/api/team-members', {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setProducts(productsResponse.data);
        setTeamMembers(teamMembersResponse.data);
      } catch (err) {
        console.error('Error fetching metadata:', err.response?.data || err.message);
        alert('Failed to load metadata.');
      }
    };
    fetchMetadata();
  }, []);

  // Fetch details of the selected order
  useEffect(() => {
    if (selectedOrderId) {
      const fetchOrderDetails = async () => {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        try {
          const response = await axios.get(`https://astrovidhya.in/api/orders/${selectedOrderId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setFormData(response.data);
        } catch (err) {
          console.error('Error fetching order details:', err.response?.data || err.message);
          alert('Failed to load order details.');
        } finally {
          setLoading(false);
        }
      };
      fetchOrderDetails();
    }
  }, [selectedOrderId]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle products dropdown
  const handleProductChange = (e) => {
    const { options } = e.target;
    const selectedProducts = Array.from(options)
      .filter((option) => option.selected)
      .map((option) => option.value);
    setFormData((prev) => ({ ...prev, products: selectedProducts }));
  };

  // Handle order update
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    try {
      await axios.put(`https://astrovidhya.in/api/orders/${selectedOrderId}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Order updated successfully!');
    } catch (err) {
      console.error('Error updating order:', err.response?.data || err.message);
      alert('Failed to update order.');
    }
  };

  return (
    <div className="order-edit bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">Edit Order</h2>

      {/* Dropdown to select an order */}
      <div className="mb-4">
        <label className="block text-gray-700 font-medium mb-2">Select Order</label>
        <select
          value={selectedOrderId}
          onChange={(e) => setSelectedOrderId(e.target.value)}
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">-- Select an Order --</option>
          {orders.map((order) => (
            <option key={order._id} value={order._id}>
              {order._id} - {order.customer_name}
            </option>
          ))}
        </select>
      </div>

      {/* Order Details Form */}
      {loading && <p>Loading order details...</p>}
      {formData && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">Customer Name</label>
            <input
              type="text"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Order Date</label>
            <input
              type="date"
              name="order_date"
              value={formData.order_date}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="Pending">Pending</option>
              <option value="Processing">Processing</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Total Amount</label>
            <input
              type="number"
              name="total_amount"
              value={formData.total_amount}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Products</label>
            <select
              multiple
              name="products"
              value={formData.products}
              onChange={handleProductChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {products.map((product) => (
                <option key={product._id} value={product._id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Shipping Address</label>
            <textarea
              name="shipping_address"
              value={formData.shipping_address}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              required
            ></textarea>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Payment Status</label>
            <select
              name="payment_status"
              value={formData.payment_status}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="Paid">Paid</option>
              <option value="Unpaid">Unpaid</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Approval Status</label>
            <select
              name="approval_status"
              value={formData.approval_status}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Assigned To</label>
            <select
              name="assigned_to"
              value={formData.assigned_to}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">-- Select a Team Member --</option>
              {teamMembers.map((member) => (
                <option key={member._id} value={member._id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Submit Changes
          </button>
        </form>
      )}
    </div>
  );
};

export default OrderEdit;
